import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { lettersApi } from '../services/api';
import { LetterDocument } from '../components/letters/LetterDocument';
import { formatSeplReferenceNumber } from '../utils/helpers';

interface QRLetterResponse {
  success: boolean;
  letter?: {
    id: number;
    serial_number: number;
    letter_number: string;
    subject: string;
    letter_date: string;
    body_html: string;
    letterhead_html: string;
    footer_html: string;
    approved_signature_html?: string | null;
    qr_code_data?: string | null;
  };
  error?: string;
}

export function QRLetterAccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [letter, setLetter] = useState<QRLetterResponse['letter'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // App.tsx renders this page directly (without a <Route path="/qr-letter/:token">)
  // so useParams() is always empty here. Parse token from pathname explicitly.
  const token = useMemo(() => {
    const prefix = '/qr-letter/';
    if (!location.pathname.startsWith(prefix)) return '';
    const raw = location.pathname.slice(prefix.length).split('/')[0].trim();
    if (!raw) return '';
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadLetter = async () => {
      if (!token) {
        setError('Invalid QR code token.');
        setLoading(false);
        return;
      }

      try {
        const response = await lettersApi.verifyQRToken(token);
        const data = response.data as QRLetterResponse;
        if (!cancelled) {
          if (data.success && data.letter) {
            setLetter(data.letter);
            setError(null);
          } else {
            setLetter(null);
            setError(data.error || 'Failed to verify QR code.');
          }
        }
      } catch (err) {
        console.error('Error verifying QR token:', err);
        if (!cancelled) {
          setLetter(null);
          setError('Failed to verify QR code. The code may be invalid or expired.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadLetter();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const referenceNumber = useMemo(
    () => (letter ? formatSeplReferenceNumber(letter.letter_number) || 'Auto-generated' : ''),
    [letter]
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">Public QR Access</p>
            <h1 className="truncate text-lg font-semibold text-slate-950 sm:text-xl">Verified Letter</h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-10">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-emerald-600" />
            <p className="text-sm font-medium text-slate-600">Verifying QR code...</p>
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 shadow-sm sm:p-8">
            <div className="flex items-start gap-3 sm:gap-4">
              <AlertCircle className="mt-0.5 h-6 w-6 text-rose-600" />
              <div>
                <h2 className="text-base font-semibold text-rose-900 sm:text-lg">Access denied</h2>
                <p className="mt-2 text-sm text-rose-700">{error}</p>
                <p className="mt-4 text-sm text-rose-600">This QR link only works for a valid approved letter.</p>
              </div>
            </div>
          </div>
        ) : letter ? (
          <div className="space-y-4 sm:space-y-6">
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-4 shadow-sm sm:px-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-6 w-6 flex-none text-emerald-700" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">No login required</p>
                  <p className="mt-1 text-sm leading-6 text-emerald-800">
                    This letter is open for guest viewing through a valid QR scan.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Number</div>
                  <div className="mt-1 text-sm font-medium text-slate-900">{referenceNumber}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    {new Date(letter.letter_date).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</div>
                  <div className="mt-1 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                    Approved
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</div>
                  <div className="mt-1 truncate text-sm font-medium text-slate-900">{letter.subject}</div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-6 sm:py-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600">Letter Preview</h2>
              </div>
              <div className="relative overflow-hidden">
                <div className="overflow-x-auto overflow-y-hidden p-3 sm:p-4 lg:p-6">
                  <div className="min-w-[794px]">
                    <LetterDocument
                      subject={letter.subject}
                      letterDate={letter.letter_date}
                      serialNumber={letter.serial_number}
                      letterNumber={letter.letter_number}
                      bodyHtml={letter.body_html}
                      letterheadHtml={letter.letterhead_html}
                      footerHtml={letter.footer_html}
                      approvedSignatureHtml={letter.approved_signature_html}
                      qrCodeData={letter.qr_code_data}
                      blurApprovedSignature
                      fitViewport
                    />
                  </div>
                </div>

                {/* Keep top reference region readable, then fully hide everything below it. */}
                <div className="pointer-events-none absolute inset-x-0 top-[172px] bottom-0 z-20">
                  <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-transparent to-white/35 backdrop-blur-[2px]" />
                  <div className="absolute inset-x-0 top-6 bottom-0 bg-white/55 backdrop-blur-[16px]" />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}